import { useState, useEffect, useCallback, useMemo } from "react";

import { ChevronDown, X } from "lucide-react";
import { Button } from "./ui/button";
import { Skeleton } from "../components/ui/skeleton";
import coin from "../assets/images/placeholder/crypto-colour.png";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "./ui/dialog";
import CurrencyFormatter from "../utils/currencyFormatter";
import { useCryptoAssets } from "../hooks/query/queries";
import { formatFiatWithTwoDecimals } from "../utils/numberFormatter";
export default function AssetSaleSelectorForex({
  onSelect,
  awaitingSaleRate,
  setAwaitingSaleRate,
  userId,
  hotActionAsset,
  currencies,
  setSelectedSaleAsset,
  selectedSaleAsset,
  fiatBalances,
}) {
  const { isLoading } = useCryptoAssets();

  const [searchTerm, setSearchTerm] = useState("");

  const [open, setOpen] = useState(false);
  const formatter = new CurrencyFormatter();

  const enrichedCurrencies = useMemo(() => {
    if (!currencies) return undefined;

    return currencies.map((asset) => {
      if (fiatBalances && fiatBalances.length > 0) {
        const fiatBalance = fiatBalances.find(
          (balance) =>
            balance.asset?.toLowerCase() === asset.asset?.toLowerCase()
        );

        if (fiatBalance?.valueInLocalCurrency) {
          return {
            ...asset,
            valueInLocalCurrency: fiatBalance.valueInLocalCurrency,
          };
        }
      }

      return asset;
    });
  }, [currencies, fiatBalances]);

  const filteredAssets = enrichedCurrencies?.filter(
    (assets) =>
      assets?.asset?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      assets?.assetName?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const handleSelect = useCallback(
    (asset) => {
      if (asset?.asset === selectedSaleAsset?.asset) {
        setSelectedSaleAsset(asset);
        setOpen(false);
        return;
      }
      if (asset?.asset !== selectedSaleAsset?.asset) {
        setAwaitingSaleRate(true);
        setSelectedSaleAsset(asset);
        setOpen(false);
        if (onSelect) {
          onSelect(asset);
        }
      }
    },
    [selectedSaleAsset, setSelectedSaleAsset, setAwaitingSaleRate, onSelect]
  );

  useEffect(() => {
    if (!hotActionAsset) return;
    handleSelect(hotActionAsset);
  }, [hotActionAsset, handleSelect]);

  return (
    <div className="grid grid-cols-4 gap-4 grid-rows-1 py-8">
      <div className="space-y-2 col-span-1">
        <label className="text-gray-600 text-sm">Select asset</label>
        <Dialog open={open} onOpenChange={setOpen} modal={false}>
          <DialogTrigger asChild>
            {isLoading ? (
              <div className="w-full flex items-center justify-between py-4  px-4 rounded-2xl border border-gray-300 bg-muted/50">
                <div className="flex items-center gap-3 ">
                  <Skeleton className="w-10 h-10 rounded-full bg-gray-300" />

                  <div className="flex flex-col gap-1">
                    <Skeleton className="w-10 h-5 rounded-full bg-gray-300" />
                  </div>
                </div>
                <Skeleton className="w-6 h-6 rounded-full bg-gray-300" />
              </div>
            ) : (
              <Button
                disabled={awaitingSaleRate}
                variant="outline"
                className="w-full flex items-center justify-between py-8 rounded-2xl hover:border-brand-primary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10  rounded-full flex items-center justify-center">
                    {selectedSaleAsset ? (
                      <img
                        src={selectedSaleAsset?.assetIcon}
                        alt={selectedSaleAsset?.assetName}
                        width={32}
                        height={32}
                        className="text-white"
                      />
                    ) : (
                      <img
                        src={coin}
                        alt={"Default Asset"}
                        width={32}
                        height={32}
                        className="text-white"
                      />
                    )}
                  </div>
                  <div className="text-left">
                    <div className="font-semibold">
                      {selectedSaleAsset?.asset.toUpperCase()}
                    </div>
                    <div className="text-gray-600">
                      {selectedSaleAsset?.assetName
                        ? selectedSaleAsset.assetName.length > 10
                          ? selectedSaleAsset.assetName.slice(0, 10) + "..."
                          : selectedSaleAsset.assetName
                        : "Select..."}
                    </div>
                  </div>
                </div>
                <ChevronDown className="text-gray-400" />
              </Button>
            )}
          </DialogTrigger>

          <DialogContent className="max-w-sm rounded-2xl p-6">
            <DialogClose asChild>
              <Button
                variant="ghost"
                onClick={() => setOpen(false)}
                className="w-10 h-10 absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-full p-0 flex items-center justify-center border border-gray-200 hover:bg-gray-100"
              >
                <X size={18} className="text-gray-700" />
              </Button>
            </DialogClose>

            <DialogHeader>
              <DialogTitle className="text-lg font-semibold text-gray-800">
                Select an Asset
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <input
                type="text"
                placeholder="Search addresses..."
                className="w-full border rounded-lg px-3 py-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="mt-4 flex flex-col space-y-3 max-h-72 overflow-y-auto">
              {currencies &&
                filteredAssets?.map((asset) => {
                  return (
                    <button
                      key={asset.assetId}
                      onClick={() => {
                        handleSelect(asset);
                      }}
                      className="flex items-center gap-3 justify-between px-3 py-3 border rounded-lg border-transparent bg-gray-50 hover:border-rose-200"
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={asset.assetIcon}
                          alt={asset.assetName}
                          className="w-6 h-6"
                        />
                        <div className="flex flex-col text-left">
                          <span>{asset.assetName}</span>
                          <span className="text-xs text-gray-500">
                            {asset.asset?.toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="font-bold">
                          {formatter.format(
                            asset.availableAmount,
                            asset.asset?.toUpperCase()
                          )}
                        </span>
                        <span className="text-xs text-gray-500">
                          {asset.valueInLocalCurrency
                            ? formatFiatWithTwoDecimals(
                                asset.valueInLocalCurrency
                              )
                            : "R 0.00"}
                        </span>
                      </div>
                    </button>
                  );
                })}
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="space-y-2 col-span-3">
        <label className="text-gray-600 text-sm">Available balance</label>
        <div className="p-5 rounded-2xl bg-gray-50 flex-1 flex items-center">
          <div className="text-xl font-semibold text-gray-600">
            {selectedSaleAsset?.availableAmountFormatted}
          </div>
        </div>
      </div>
    </div>
  );
}
